import React, { useEffect, useState } from "react";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import { Badge } from "components/common/Badge";
import { SectionRule } from "components/common/SectionRule";
import { Tooltip } from "components/common/Tooltip";
import { plural } from "components/common/setUtils";
import { MetaAddButton } from "components/main/datasets/DatasetMetaSection";
import { ListCount } from "components/main/pages/pageColumn";
import { OrgMark } from "@/components/main/organizations/OrgMark";
import { orgCreateRefusal } from "@/components/main/organizations/orgCreateRefusal";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";
import {
  fetchOrgLimits,
  type Org,
  type OrgLimits,
} from "@/lib/api/organizations";

const roleLabel = (org: Org): string => {
  // A personal org is named "Personal" until its owner renames it.
  if (org.is_personal)
    return org.name.toLowerCase() === "personal" ? "" : "Personal";
  if (org.role === "owner") return "Owner";
  if (org.role === "org_admin") return "Admin";
  return org.role ? "Member" : "";
};

/** Without `onCreateOrg` it only lists (the account card): the count rides the
 *  title's end. With it (the personal org's General), the count and New
 *  organization sit under the list. */
export const OrgList: React.FC<{
  onOpenOrg: (org: Org) => void;
  onCreateOrg?: () => void;
}> = ({ onOpenOrg, onCreateOrg }) => {
  const orgs = useOrgContextStore((s) => s.orgs);
  // A boolean, not the callback: the caller passes a fresh one every render.
  const canCreateOrg = !!onCreateOrg;
  const [orgLimits, setOrgLimits] = useState<OrgLimits | null>(null);
  useEffect(() => {
    if (!canCreateOrg) return;
    void fetchOrgLimits()
      .then(setOrgLimits)
      .catch(() => setOrgLimits(null));
  }, [canCreateOrg]);
  if (orgs.length === 0) return null;
  const sorted = [...orgs].sort(
    (a, b) =>
      Number(b.is_personal) - Number(a.is_personal) ||
      a.name.localeCompare(b.name),
  );

  return (
    <section aria-label="Organizations">
      <SectionRule
        label="Organizations"
        trailing={onCreateOrg ? undefined : sorted.length}
      />
      <div className="flex flex-col">
        {sorted.map((org) => (
          <button
            key={org.org_id}
            type="button"
            className="settings-row"
            aria-label={`Open settings for ${org.name}`}
            onClick={() => onOpenOrg(org)}
          >
            <OrgMark org={org} size="sm" />
            <span className="min-w-0 truncate text-left text-fg-regular">
              {org.name}
            </span>
            <NorthEastIcon className="settings-row-go" sx={{ fontSize: 12 }} />
            <span className="ml-auto text-fg-muted-regular-small">
              {roleLabel(org)}
            </span>
            <span className="settings-org-plan">
              {org.plan_name && <Badge label={org.plan_name} />}
            </span>
          </button>
        ))}
      </div>
      {/* Overview's footer: the count where the list starts, the door at its end. */}
      {onCreateOrg && (
        <div className="flex items-center pt-2">
          <ListCount parts={[plural(sorted.length, "organization")]} />
          {/* Our tooltip on a wrapper: a disabled button fires no pointer
              events, so at the cap the reason would otherwise never show. */}
          <Tooltip
            title={
              orgCreateRefusal(orgLimits) ??
              "A separate roster, plan and set of spaces."
            }
          >
            <span className="ml-auto flex">
              <MetaAddButton
                label="New organization"
                disabled={orgLimits ? !orgLimits.can_create_free_org : true}
                onClick={onCreateOrg}
              />
            </span>
          </Tooltip>
        </div>
      )}
    </section>
  );
};
